import React, { useEffect, useState } from 'react';
import { Form, Input, Modal, Radio, InputNumber, Spin, notification } from 'antd';
import { useTranslation } from 'react-i18next';
import { InspectConfig, InspectConfigAdd } from '@/types/inspection';
import { Product } from '@/types/product';
import { productConfigAdd, productConfigGet, productConfigUpdate } from '@/apis/inspection';
import ProductDesc from '@/components/Desc/ProductDesc';

interface ProductInspectConfigProps {
  product: Product;
  open: boolean;
  onClose: () => void;
}

const ProductInspectConfig: React.FC<ProductInspectConfigProps> = ({ product, open, onClose }) => {
  const { t } = useTranslation();
  const [form] = Form.useForm<InspectConfigAdd>();
  const [loading, setLoading] = useState<boolean>(false);
  const [configDtl, setConfigDtl] = useState<InspectConfig | null>(null);

  useEffect(() => {
    if (open) initData();
  }, [open, form, product]);

  const initData = async () => {
    try {
      setLoading(true);
      let config = await productConfigGet(product.id);
      if (config) {
        form.setFieldsValue({ ...config });
        setConfigDtl({ ...config });
      }
      setLoading(false);
    } catch (error) { setLoading(false); };
  };

  const handleOk = async (values: InspectConfigAdd) => {
    try {
      setLoading(true);
      if (!!!configDtl) {
        let newData = await productConfigAdd(product.id, { ...values });
        setConfigDtl({ ...newData })
      }
      else await productConfigUpdate(product.id, configDtl.id, { ...values });
      setLoading(false);
      notification.success({ message: t('inspection.saveOk') });
    } catch (error) {
      notification.error({ message: t('inspection.saveErr') });
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      title={t('inspection.config')}
      onOk={form.submit}
      onCancel={onClose}
      destroyOnClose
      width={600}
    >
      <Spin spinning={loading}>
        <Form form={form} layout="vertical" onFinish={handleOk}>
          <Form.Item noStyle>
            <ProductDesc product={product} showtype="SUPPLIER" />
          </Form.Item>
          <Form.Item layout='horizontal'
            name="needInspection"
            label={t('inspection.needInspection')}
            rules={[{ required: true, message: t('inspection.plsSelectIns') }]}
            style={{ marginTop: 24 }}
          >
            <Radio.Group>
              <Radio value={1}>{t('inspection.yes')}</Radio>
              <Radio value={0}>{t('inspection.no')}</Radio>
            </Radio.Group>
          </Form.Item>
          <Form.Item
            name="cycleDays"
            label={t('inspection.cycleDays')}
          >
            <InputNumber changeOnWheel min={1} precision={0}
              style={{ width: '100%' }}
              placeholder={t('inspection.cycleDaysPlaceholder')}
              addonAfter={t('inspection.day')}
            />
          </Form.Item>
          <Form.Item
            name="content"
            label={t('inspection.content')}
          >
            <Input.TextArea rows={3} placeholder={t('inspection.content')} />
          </Form.Item>
          <Form.Item
            name="remark"
            label={t('inspection.remark')}
          >
            <Input.TextArea rows={3} placeholder={t('inspection.remark')} />
          </Form.Item>
        </Form>
      </Spin>
    </Modal>
  );
};

export default ProductInspectConfig;
